// Aidea Assistant (agent) guide — the chat panel that can generate and edit
// scene content on request, plus the live course-build view. Locale-keyed like
// every guide (content.<lang> → { feature, title, sections }).

const assistantGuide = {
  id: "assistant",
  icon: "forum",
  keywords: [
    "agent",
    "ajan",
    "ai agent",
    "yapay zeka asistanı",
    "assistant",
    "asistan",
    "aidea assistant",
    "aidea asistan",
    "chat",
    "sohbet",
    "chatbot",
    "sohbet botu",
    "ai chat",
    "conversation",
    "konuşma",
    "generate",
    "oluştur",
    "narration",
    "seslendirme",
    "anlatım",
    "bulk",
    "toplu",
    "font",
    "yazı tipi",
    "voice",
    "ses",
    "motion",
    "hareket",
    "motion graphics",
    "hareketli grafik",
    "animate",
    "hareketlendir",
    "course build",
    "eğitim oluşturma",
    "build",
    "scene 1",
    "sahne 1",
    "continue",
    "devam et",
    "improve",
    "iyileştir",
    "mind map",
    "zihin haritası",
    "course brief",
    "eğitim özeti",
    "edit image",
    "görsel düzenle",
    "help",
    "yardım",
    "how to",
    "nasıl",
    "asistente",
    "asistente aidea",
    "agente de ia",
    "conversación",
    "narración",
    "voz",
    "fuente",
    "gráficos animados",
    "animar escena",
    "creación del curso",
    "mapa mental",
    "resumen del curso",
    "editar imagen",
    "ayuda",
    "cómo",
  ],
  content: {
    en: {
      feature: "AI",
      title: "Aidea Assistant",
      sections: [
        {
          id: "overview",
          title: "Opening the Assistant",
          videoUrl: null,
          text:
            "Click the bot icon at the start of the AI tools group in the top navbar (tooltip **AI Agent**) to open the **Aidea Assistant** panel (marked **Beta**) in the bottom-right corner. Type what you want in plain language — the Assistant replies in the editor's language. Drag the panel's top-left corner to resize it.\n\n" +
            "- The panel icon at the left of the header shows or hides your conversations. **New Thread** starts a fresh one, and the trash icon next to a conversation deletes it.\n" +
            "- Conversations are kept per training on this browser, so you can switch back to an earlier one without losing its history.\n" +
            "- When the **Chatbot** toggle is on in **Global Configuration**, the round launcher button in the bottom-right corner also offers **Chatbot** (the Assistant) next to **Widget** (the job queue).",
        },
        {
          id: "what-it-does",
          title: "What it can do",
          videoUrl: null,
          text:
            "The empty chat shows a greeting and quick-start cards. Clicking a card puts a ready-made request into the message box so you can adjust it before sending:\n\n" +
            "- **Create an infographic scene** — a new scene that summarizes the key points.\n" +
            "- **Generate an image** — an image illustrating the main concept.\n" +
            "- **Update narration** — new narration for this scene.\n" +
            "- **Add avatar** — a talking avatar presenting this scene.\n" +
            "- **Generate video** — a video clip for this scene.\n" +
            "- **Animate this scene (Motion)** — motion graphics designed for this scene.\n\n" +
            "For media requests the Assistant usually opens the matching Studio window — image generator, AI voice, avatar, video or **Infographic Scene** — already filled in with its prompt, narration or chosen style; you review and generate there. Some answers come as a card first, for example style tiles to pick from. If a window didn't appear, use the **Open …** button on the card. While such a window is open, the chat shrinks to a slim rail on the right.\n\n" +
            'It can also make **changes across every scene** (see below) and answer **"how do I…"** questions with step-by-step instructions.',
        },
        {
          id: "tips",
          title: "Tips",
          videoUrl: null,
          text:
            "- It works on the **current scene** (and the selected element) — open the scene you want changed before asking.\n" +
            "- Generated media is only added when you finish it in the Studio window the Assistant opens, or click **Add to scene**. Actions that cost credits show the cost on the card and wait for your confirmation.\n" +
            "- Images that finish generating appear above the message box under **Generated images**, each with an **Add to scene** button.\n" +
            "- **Enter** sends your message, **Shift+Enter** starts a new line. The Assistant can make mistakes — check its changes.",
        },
        {
          id: "bulk-changes",
          title: "Changes across all scenes",
          videoUrl: null,
          text:
            "Ask for a change that should apply everywhere and the Assistant shows a card for it:\n\n" +
            "- **One font on every scene** or **one narration voice for every scene** — applied right away; the card shows what changed and an **Undo** button. A new narration voice is used for narration generated from then on — existing audio is not re-recorded automatically.\n" +
            "- **Animate all images in a scene** — the card shows the credit cost and waits for **Apply** (or **Cancel**). It then starts one animation job per image and shows their progress.",
        },
        {
          id: "motion-graphics",
          title: "Animating a scene (Motion)",
          videoUrl: null,
          text:
            "Use the **Animate this scene (Motion)** card or ask the Assistant to animate the current scene. It designs on-screen text, readability scrims and icons from the scene's images and narration — so the scene needs at least one image first.\n\n" +
            "- Choose **On-screen text** (**Key phrases** or **All text**) and **Graphics** (**Minimal**, **Balanced** or **Rich**).\n" +
            "- The card shows the maximum credit cost. Click **Design motion graphics** to start — it takes 1–3 minutes and you can keep working meanwhile.\n" +
            "- When the plan is ready, click **Apply to scene**. A single **Undo** removes everything it added.",
        },
        {
          id: "course-build",
          title: "Building a course from chat",
          videoUrl: null,
          text:
            "When you start a new training from the chat in the Aidea console, Studio opens in build mode and the same conversation continues here. Next to the chat, a live preview shows the scene being built — its images, a snippet of its narration and the list of planned scenes. Each scene takes about 2–4 minutes.\n\n" +
            "- The editor appears as soon as scene 1 is ready. Until the build finishes, editing is frozen (you can still preview), saving stays locked, and scenes still being built show as **(building)** in the scene list.\n" +
            "- When the build finishes, a new conversation opens with a **Your training is ready** message — ask for any change: narration, visuals, a new scene.\n" +
            "- The build conversation stays available, read-only, as **Course build** in your conversation list.",
        },
        {
          id: "build-review",
          title: "Reviewing scene 1",
          videoUrl: null,
          text:
            "The build pauses after scene 1 so you can check it. The card **Scene 1 is ready — take a look.** shows how many scenes are left and roughly how many credits they will use.\n\n" +
            '- **Continue** — builds the remaining scenes. Typing "continue" in the chat does the same.\n' +
            "- **Improve scene 1** — write what should change, then click **Rebuild scene 1**. Anything else you type in the chat while the build is paused is also treated as a note for scene 1.\n" +
            "- If there aren't enough credits for the remaining scenes, the card tells you — top up and try again.",
        },
        {
          id: "build-progress",
          title: "Build progress, mind map and course brief",
          videoUrl: null,
          text:
            "Before the editor appears, the build preview walks you through the **AI work stages**: planning the course, choosing the style and design, recording the narration voice, building scene 1, the pause for your review, and the remaining scenes.\n\n" +
            "- **Mind map** — shows a mind map of the course in place of the preview; **Back to progress** returns to it. The button reads **Preparing mind map…** until the map is ready.\n" +
            "- **Course brief** — the learning objectives, themes, and each scene's narration, so you can read the plan while scene 1 is being built.",
        },
        {
          id: "edit-image-chat",
          title: "Editing an image through chat",
          videoUrl: null,
          text:
            "Select an image on the canvas and open **Animate Image** from its toolbar. The chat icon in that window's top bar (tooltip **Edit with chatbot**) hands the image to the Assistant: the window closes, the chat opens with a starter sentence, and you describe the edit.\n\n" +
            "- Each edit is previewed in the chat; keep writing to refine it further.\n" +
            "- The image on the canvas only changes when you click **Uygula** (Apply) on the card.",
        },
      ],
    },
    tr: {
      feature: "Yapay Zeka",
      title: "Aidea Asistan",
      sections: [
        {
          id: "overview",
          title: "Asistanı açma",
          videoUrl: null,
          text:
            "Üst gezinme çubuğunda yapay zeka araçları grubunun başındaki robot simgesine (araç ipucu **Yapay Zeka Asistanı**) tıklayın; sağ alt köşede **Aidea Asistan** paneli (**Beta**) açılır. İsteğinizi sade bir dille yazmanız yeterlidir — asistan editörün dilinde yanıt verir. Panelin sol üst köşesini sürükleyerek boyutunu değiştirebilirsiniz.\n\n" +
            "- Başlığın solundaki panel simgesi konuşmalarınızı gösterir veya gizler. **Yeni Konuşma** yeni bir sohbet başlatır; bir konuşmanın yanındaki çöp kutusu simgesi onu siler.\n" +
            "- Konuşmalar bu tarayıcıda eğitim bazında saklanır; böylece geçmişi kaybetmeden önceki bir konuşmaya dönebilirsiniz.\n" +
            "- **Genel Konfigürasyon** içindeki **Sohbet Botu** anahtarı açıksa, sağ alt köşedeki yuvarlak başlatıcı düğme **Widget** (iş kuyruğu) seçeneğinin yanında **Chatbot** (asistan) seçeneğini de sunar.",
        },
        {
          id: "what-it-does",
          title: "Neler yapabilir",
          videoUrl: null,
          text:
            "Boş sohbet ekranında bir karşılama mesajı ve hızlı başlangıç kartları görünür. Bir karta tıklamak, hazır bir isteği mesaj kutusuna yazar; göndermeden önce düzenleyebilirsiniz:\n\n" +
            "- **İnfografik sahne oluştur** — ana noktaları özetleyen yeni bir sahne.\n" +
            "- **Görsel oluştur** — ana kavramı anlatan bir görsel.\n" +
            "- **Anlatımı güncelle** — bu sahne için yeni bir anlatım.\n" +
            "- **Avatar ekle** — bu sahneyi sunan konuşan bir avatar.\n" +
            "- **Video oluştur** — bu sahne için bir video klip.\n" +
            "- **Bu sahneyi hareketlendir** — bu sahne için tasarlanmış hareketli grafikler.\n\n" +
            "Medya isteklerinde asistan genellikle ilgili Studio penceresini — görsel oluşturucu, yapay zeka sesi, avatar, video veya **İnfografik Sahne** — istem, anlatım ya da seçilen stille önceden doldurulmuş olarak açar; kontrol edip üretimi orada yaparsınız. Bazı yanıtlar önce bir kart olarak gelir; örneğin aralarından seçim yapacağınız stil kutucukları. Pencere açılmadıysa karttaki **Open …** (Aç) düğmesini kullanın. Böyle bir pencere açıkken sohbet sağda ince bir şeride küçülür.\n\n" +
            'Ayrıca **tüm sahnelerde değişiklik** yapabilir (aşağıya bakın) ve **"nasıl yapılır"** sorularını adım adım talimatlarla yanıtlayabilir.',
        },
        {
          id: "tips",
          title: "İpuçları",
          videoUrl: null,
          text:
            "- **Geçerli sahne** (ve seçili öğe) üzerinde çalışır — değiştirmek istediğiniz sahneyi açtıktan sonra isteğinizi yazın.\n" +
            "- Üretilen medya, yalnızca asistanın açtığı Studio penceresinde işlemi tamamladığınızda veya **Add to scene** (Sahneye ekle) düğmesine tıkladığınızda eklenir. Kredi harcayan işlemler kartta maliyeti gösterir ve onayınızı bekler.\n" +
            "- Üretimi tamamlanan görseller mesaj kutusunun üstünde **Generated images** (Üretilen görseller) başlığı altında, her biri **Add to scene** (Sahneye ekle) düğmesiyle görünür.\n" +
            "- **Enter** mesajı gönderir, **Shift+Enter** yeni satıra geçer. Aidea hata yapabilir — yaptığı değişiklikleri kontrol edin.",
        },
        {
          id: "bulk-changes",
          title: "Tüm sahnelerde değişiklik",
          videoUrl: null,
          text:
            "Her yerde geçerli olmasını istediğiniz bir değişikliği isteyin; asistan bunun için bir kart gösterir:\n\n" +
            "- **Tüm sahnelerde aynı yazı tipi** veya **tüm sahnelerde aynı anlatım sesi** — hemen uygulanır; kart neyin değiştiğini ve bir **Undo** (Geri al) düğmesini gösterir. Yeni anlatım sesi, bundan sonra üretilen anlatımlarda kullanılır — mevcut sesler otomatik olarak yeniden kaydedilmez.\n" +
            "- **Bir sahnedeki tüm görselleri canlandırma** — kart kredi maliyetini gösterir ve **Apply** (Uygula) ya da **Cancel** (İptal) seçmenizi bekler. Ardından her görsel için bir canlandırma işi başlatır ve ilerlemelerini gösterir.",
        },
        {
          id: "motion-graphics",
          title: "Sahneyi hareketlendirme (Motion)",
          videoUrl: null,
          text:
            "**Bu sahneyi hareketlendir** kartını kullanın veya asistandan geçerli sahneyi hareketlendirmesini isteyin. Sahnenin görsellerinden ve anlatımından ekran yazıları, okunabilirlik katmanları ve ikonlar tasarlar — bu yüzden sahnede önce en az bir görsel olmalıdır.\n\n" +
            "- **Ekran yazıları** (**Önemli metinler** veya **Tüm metinler**) ve **Grafikler** (**Sade**, **Dengeli** veya **Zengin**) seçin.\n" +
            "- Kart, en yüksek kredi maliyetini gösterir. Başlatmak için **Hareketli grafik tasarla**'ya tıklayın — 1–3 dakika sürer ve bu sırada çalışmaya devam edebilirsiniz.\n" +
            "- Plan hazır olduğunda **Sahneye uygula**'ya tıklayın. Tek bir **Geri al**, eklenen her şeyi kaldırır.",
        },
        {
          id: "course-build",
          title: "Sohbetten eğitim oluşturma",
          videoUrl: null,
          text:
            "Aidea konsolundaki sohbetten yeni bir eğitim başlattığınızda Studio oluşturma modunda açılır ve aynı konuşma burada devam eder. Sohbetin yanındaki canlı önizleme, oluşturulan sahneyi — görsellerini, anlatımından bir bölümü ve planlanan sahnelerin listesini — gösterir. Her sahne yaklaşık 2–4 dakika sürer.\n\n" +
            "- Editör, 1. sahne hazır olur olmaz görünür. Oluşturma bitene kadar düzenleme kapalıdır (önizleme yapabilirsiniz), kaydetme kilitli kalır ve henüz oluşturulan sahneler sahne listesinde **(hazırlanıyor)** olarak görünür.\n" +
            "- Oluşturma bittiğinde **Eğitiminiz hazır** mesajıyla yeni bir konuşma açılır — anlatım, görseller veya yeni bir sahne gibi istediğiniz değişikliği isteyin.\n" +
            "- Oluşturma konuşması, konuşma listenizde salt okunur olarak **Eğitim oluşturma** adıyla kalır.",
        },
        {
          id: "build-review",
          title: "1. sahneyi inceleme",
          videoUrl: null,
          text:
            "Oluşturma, kontrol edebilmeniz için 1. sahneden sonra duraklar. **Sahne 1 hazır — bir göz atın.** kartı kaç sahne kaldığını ve bunların yaklaşık kaç kredi harcayacağını gösterir.\n\n" +
            '- **Devam et** — kalan sahneleri oluşturur. Sohbete "devam" yazmak da aynı işi yapar.\n' +
            "- **Sahne 1'i iyileştir** — neyin değişmesi gerektiğini yazın, ardından **Sahne 1'i yeniden oluştur**'a tıklayın. Oluşturma duraklatılmışken sohbete yazdığınız diğer her şey de 1. sahne için bir not olarak değerlendirilir.\n" +
            "- Kalan sahneler için yeterli kredi yoksa kart bunu belirtir — kredi yükleyip tekrar deneyin.",
        },
        {
          id: "build-progress",
          title: "Oluşturma ilerlemesi, zihin haritası ve eğitim özeti",
          videoUrl: null,
          text:
            "Editör görünmeden önce oluşturma önizlemesi **Yapay zekâ çalışma aşamaları**'nı gösterir: eğitimin planlanması, stil ve tasarımın seçilmesi, anlatım sesinin hazırlanması, 1. sahnenin oluşturulması, incelemeniz için duraklama ve kalan sahneler.\n\n" +
            "- **Zihin haritası** — önizlemenin yerinde eğitimin zihin haritasını gösterir; **İlerlemeye dön** önizlemeye geri götürür. Harita hazır olana kadar düğmede **Zihin haritası hazırlanıyor…** yazar.\n" +
            "- **Eğitim özeti** — öğrenme hedefleri, temalar ve her sahnenin anlatımı; böylece 1. sahne oluşturulurken planı okuyabilirsiniz.",
        },
        {
          id: "edit-image-chat",
          title: "Görseli sohbetle düzenleme",
          videoUrl: null,
          text:
            "Tuvalde bir görsel seçin ve araç çubuğundan **Görseli Canlandır**'ı açın. Bu pencerenin üst çubuğundaki sohbet simgesi (araç ipucu **Chatbot ile düzenle**) görseli asistana aktarır: pencere kapanır, sohbet bir başlangıç cümlesiyle açılır ve yapmak istediğiniz düzenlemeyi yazarsınız.\n\n" +
            "- Her düzenleme sohbette önizlenir; daha da iyileştirmek için yazmaya devam edin.\n" +
            "- Tuvaldeki görsel yalnızca karttaki **Uygula** düğmesine tıkladığınızda değişir.",
        },
      ],
    },
    es: {
      feature: "IA",
      title: "Asistente Aidea",
      sections: [
        {
          id: "overview",
          title: "Abrir el Asistente",
          videoUrl: null,
          text:
            "Haz clic en el icono del robot al principio del grupo de herramientas de IA de la barra de navegación superior (texto emergente **Agente de IA**) para abrir el panel **Asistente Aidea** (marcado como **Beta**) en la esquina inferior derecha. Escribe lo que quieres con tus propias palabras: el Asistente responde en el idioma del editor. Arrastra la esquina superior izquierda del panel para cambiar su tamaño.\n\n" +
            "- El icono de panel a la izquierda del encabezado muestra u oculta tus conversaciones. **Nueva conversación** inicia una nueva, y el icono de la papelera junto a una conversación la elimina.\n" +
            "- Las conversaciones se guardan por formación en este navegador, así que puedes volver a una anterior sin perder su historial.\n" +
            "- Cuando el interruptor **Chatbot** está activado en **Configuración Global**, el botón redondo de la esquina inferior derecha también ofrece **Chatbot** (el Asistente) junto a **Widget** (la cola de trabajos).",
        },
        {
          id: "what-it-does",
          title: "Qué puede hacer",
          videoUrl: null,
          text:
            "El chat vacío muestra un saludo y tarjetas de inicio rápido. Al hacer clic en una tarjeta, se escribe una petición predefinida en el cuadro de mensaje para que puedas ajustarla antes de enviarla:\n\n" +
            "- **Crear una escena infográfica**: una escena nueva que resume los puntos clave.\n" +
            "- **Generar una imagen**: una imagen que ilustra el concepto principal.\n" +
            "- **Actualizar narración**: una narración nueva para esta escena.\n" +
            "- **Añadir avatar**: un avatar parlante que presenta esta escena.\n" +
            "- **Generar vídeo**: un clip de vídeo para esta escena.\n" +
            "- **Animar esta escena (Motion)**: gráficos animados diseñados para esta escena.\n\n" +
            "En las peticiones de contenido multimedia, el Asistente suele abrir la ventana correspondiente de Studio (generador de imágenes, voz con IA, avatar, vídeo o **Escena Infográfica**) ya rellenada con su prompt, su narración o el estilo elegido; la revisas y generas el contenido allí. Algunas respuestas llegan primero como una tarjeta, por ejemplo con estilos para elegir. Si no se ha abierto ninguna ventana, usa el botón **Open …** (Abrir) de la tarjeta. Mientras esa ventana está abierta, el chat se reduce a una franja estrecha a la derecha.\n\n" +
            "También puede hacer **cambios en todas las escenas** (ver más abajo) y responder preguntas del tipo **«¿cómo hago…?»** con instrucciones paso a paso.",
        },
        {
          id: "tips",
          title: "Consejos",
          videoUrl: null,
          text:
            "- Trabaja sobre la **escena actual** (y el elemento seleccionado): abre la escena que quieres cambiar antes de pedírselo.\n" +
            "- El contenido generado solo se añade cuando lo terminas en la ventana de Studio que abre el Asistente o cuando haces clic en **Add to scene** (Añadir a la escena). Las acciones que gastan créditos muestran el coste en la tarjeta y esperan tu confirmación.\n" +
            "- Las imágenes que terminan de generarse aparecen encima del cuadro de mensaje, en **Generated images** (Imágenes generadas), cada una con un botón **Add to scene**.\n" +
            "- **Enter** envía el mensaje y **Shift+Enter** crea una línea nueva. El Asistente puede equivocarse: revisa sus cambios.",
        },
        {
          id: "bulk-changes",
          title: "Cambios en todas las escenas",
          videoUrl: null,
          text:
            "Pide un cambio que deba aplicarse en todas partes y el Asistente te mostrará una tarjeta para ello:\n\n" +
            "- **Una misma fuente en todas las escenas** o **una misma voz de narración para todas las escenas**: se aplica al momento; la tarjeta muestra lo que ha cambiado y un botón **Undo** (Deshacer). La nueva voz de narración se usa en las narraciones que se generen a partir de entonces; el audio existente no se vuelve a grabar automáticamente.\n" +
            "- **Animar todas las imágenes de una escena**: la tarjeta muestra el coste en créditos y espera a que pulses **Apply** (Aplicar) o **Cancel** (Cancelar). Después inicia un trabajo de animación por imagen y muestra su progreso.",
        },
        {
          id: "motion-graphics",
          title: "Animar una escena (Motion)",
          videoUrl: null,
          text:
            "Usa la tarjeta **Animar esta escena (Motion)** o pide al Asistente que anime la escena actual. Diseña textos en pantalla, fondos de legibilidad e iconos a partir de las imágenes y la narración de la escena, así que la escena necesita al menos una imagen.\n\n" +
            "- Elige **Texto en pantalla** (**Frases clave** o **Todo el texto**) y **Gráficos** (**Mínimos**, **Equilibrados** o **Abundantes**).\n" +
            "- La tarjeta muestra el coste máximo en créditos. Haz clic en **Diseñar gráficos animados** para empezar: tarda entre 1 y 3 minutos y mientras tanto puedes seguir trabajando.\n" +
            "- Cuando el plan esté listo, haz clic en **Aplicar a la escena**. Un solo **Deshacer** elimina todo lo que se ha añadido.",
        },
        {
          id: "course-build",
          title: "Crear un curso desde el chat",
          videoUrl: null,
          text:
            "Cuando inicias una formación nueva desde el chat de la consola de Aidea, Studio se abre en modo de creación y la misma conversación continúa aquí. Junto al chat, una vista previa en directo muestra la escena que se está creando: sus imágenes, un fragmento de su narración y la lista de escenas previstas. Cada escena tarda entre 2 y 4 minutos.\n\n" +
            "- El editor aparece en cuanto la escena 1 está lista. Hasta que termina la creación, la edición está bloqueada (aunque puedes previsualizar), el guardado sigue bloqueado y las escenas que aún se están creando aparecen como **(en creación)** en la lista de escenas.\n" +
            "- Cuando termina la creación, se abre una conversación nueva con el mensaje **Tu formación está lista**: pide el cambio que quieras, ya sea en la narración, en los elementos visuales o una escena nueva.\n" +
            "- La conversación de creación sigue disponible, en solo lectura, como **Creación del curso** en tu lista de conversaciones.",
        },
        {
          id: "build-review",
          title: "Revisar la escena 1",
          videoUrl: null,
          text:
            "La creación se pausa después de la escena 1 para que puedas revisarla. La tarjeta **La escena 1 está lista — échale un vistazo.** indica cuántas escenas quedan y aproximadamente cuántos créditos usarán.\n\n" +
            '- **Continuar**: crea las escenas restantes. Escribir "continuar" en el chat hace lo mismo.\n' +
            "- **Mejorar la escena 1**: escribe qué debe cambiar y haz clic en **Rehacer la escena 1**. Cualquier otra cosa que escribas en el chat mientras la creación está en pausa también se toma como una nota para la escena 1.\n" +
            "- Si no hay créditos suficientes para las escenas restantes, la tarjeta te lo indica: recarga créditos y vuelve a intentarlo.",
        },
        {
          id: "build-progress",
          title: "Progreso de la creación, mapa mental y resumen del curso",
          videoUrl: null,
          text:
            "Antes de que aparezca el editor, la vista previa de la creación te muestra las **Etapas del trabajo de la IA**: planificación del curso, elección del estilo y el diseño, grabación de la voz de narración, creación de la escena 1, la pausa para tu revisión y las escenas restantes.\n\n" +
            "- **Mapa mental**: muestra un mapa mental del curso en lugar de la vista previa; **Volver al progreso** te devuelve a ella. El botón indica **Preparando el mapa mental…** hasta que el mapa está listo.\n" +
            "- **Resumen del curso**: los objetivos de aprendizaje, los temas y la narración de cada escena, para que puedas leer el plan mientras se crea la escena 1.",
        },
        {
          id: "edit-image-chat",
          title: "Editar una imagen desde el chat",
          videoUrl: null,
          text:
            "Selecciona una imagen en el lienzo y abre **Animar Imagen** desde su barra de herramientas. El icono de chat de la barra superior de esa ventana (texto emergente **Editar con el chatbot**) pasa la imagen al Asistente: la ventana se cierra, el chat se abre con una frase inicial y tú describes la edición.\n\n" +
            "- Cada edición se previsualiza en el chat; sigue escribiendo para afinarla.\n" +
            "- La imagen del lienzo solo cambia cuando haces clic en **Uygula** (Aplicar) en la tarjeta.",
        },
      ],
    },
  },
};

export default assistantGuide;
